import { backendApi } from "@/services/backend/api";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import {
  Component,
  Kind,
} from "@/services/backend/vbs/treatments/types/component";
import { SegmentKind } from "@/services/backend/vbs/treatments/types/segmentKind";
import { Treatment } from "@/services/backend/vbs/treatments/types/treatment";
import { Filter } from "@/shared/lib/filter/filter";
import { Formula } from "@/services/backend/vbs/treatments/types/formula";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

const treatmentApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsTreatmentCreate: build.mutation<Treatment, CreateRequest>({
      query: (request) => ({
        url: "/vbs/treatment",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "VbsTreatment", id: "LIST" }],
    }),
    vbsTreatmentCreateFromOld: build.mutation<Treatment, CreateFromOldRequest>({
      query: (request) => ({
        url: "/vbs/treatment/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "VbsTreatment", id: "LIST" }],
    }),
    vbsTreatmentShow: build.query<Treatment, ShowRequest>({
      query: (request) => ({
        url: `/vbs/treatment`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsTreatment", id: result.id }] : [],
    }),
    vbsTreatmentList: build.query<Lst<Treatment>, Partial<ListRequest>>({
      query: (request) => ({
        url: "/vbs/treatment/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsTreatment"),
    }),
    vbsTreatmentListAvailableComponentKinds: build.query<
      AvailableComponentKinds,
      ListAvailableComponentKindsRequest
    >({
      query: (request) => ({
        url: "/vbs/treatment/list-available-component-kinds",
        method: "GET",
        params: request,
      }),
    }),
    vbsTreatmentCopyBaseData: build.mutation<Treatment, CopyBaseDataRequest>({
      query: (request) => ({
        url: "/vbs/treatment/copy-base-data",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
      ],
    }),
    vbsTreatmentAddArea: build.mutation<Treatment, AddAreaRequest>({
      query: (request) => ({
        url: `/vbs/treatment/area`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentRenameArea: build.mutation<Treatment, RenameAreaRequest>({
      query: (request) => ({
        url: `/vbs/treatment/area`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentDeleteArea: build.mutation<Treatment, DeleteAreaRequest>({
      query: (request) => ({
        url: `/vbs/treatment/area`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentAddPosition: build.mutation<Treatment, AddPositionRequest>({
      query: (request) => ({
        url: `/vbs/treatment/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentDeletePosition: build.mutation<
      Treatment,
      DeletePositionRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateComponent: build.mutation<
      Treatment,
      UpdateComponentRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/component`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateCycle: build.mutation<Treatment, UpdateCycleRequest>({
      query: (request) => ({
        url: `/vbs/treatment/position/cycle`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateComment: build.mutation<Treatment, UpdateCommentRequest>({
      query: (request) => ({
        url: `/vbs/treatment/position/comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
      ],
    }),
    vbsTreatmentUpdateFormula: build.mutation<Treatment, UpdateFormulaRequest>({
      query: (request) => ({
        url: `/vbs/treatment/position/formula`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateCycleDiscountActive: build.mutation<
      Treatment,
      UpdateCycleDiscountActiveRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/cycle-discount-active`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateCycleDiscountActiveAll: build.mutation<
      Treatment,
      UpdateCycleDiscountActiveAllRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/cycle-discount-active-all`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateCycleDiscountPercentage: build.mutation<
      Treatment,
      UpdateCycleDiscountPercentageRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/cycle-discount-percentage`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateSpecialDiscountActive: build.mutation<
      Treatment,
      UpdateSpecialDiscountActiveRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/special-discount-active`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateSpecialDiscountAmount: build.mutation<
      Treatment,
      UpdateSpecialDiscountAmountRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/special-discount-amount`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdatePositionCompletion: build.mutation<
      Treatment,
      UpdatePositionCompletion
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/completion`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentUpdateActuallyTreated: build.mutation<
      Treatment,
      UpdateActuallyTreatedRequest
    >({
      query: (request) => ({
        url: `/vbs/treatment/position/actually-treated`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsTreatmentSetImmutable: build.mutation<Treatment, SetImmutableRequest>({
      query: (request) => ({
        url: `/vbs/treatment/immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
      ],
    }),
    vbsTreatmentUpdateRemark: build.mutation<Treatment, UpdateRemarkRequest>({
      query: (request) => ({
        url: `/vbs/treatment/remark`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
        "VbsConfirmation",
        "VbsContract",
        "VbsOffer",
        "VbsOrder",
        "VbsWorkOrder",
      ],
    }),
    vbsTreatmentDelete: build.mutation<Treatment, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/treatment}`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsTreatment", id: "LIST" },
        { type: "VbsTreatment", id: request.id },
        "VbsCalculation",
      ],
    }),
  }),
});

export const {
  useVbsTreatmentCreateMutation,
  useVbsTreatmentCreateFromOldMutation,
  useVbsTreatmentShowQuery,
  useVbsTreatmentListQuery,
  useVbsTreatmentListAvailableComponentKindsQuery,
  useVbsTreatmentCopyBaseDataMutation,
  useVbsTreatmentAddAreaMutation,
  useVbsTreatmentRenameAreaMutation,
  useVbsTreatmentDeleteAreaMutation,
  useVbsTreatmentAddPositionMutation,
  useVbsTreatmentDeletePositionMutation,
  useVbsTreatmentUpdateComponentMutation,
  useVbsTreatmentUpdateCycleMutation,
  useVbsTreatmentUpdateCommentMutation,
  useVbsTreatmentUpdateFormulaMutation,
  useVbsTreatmentUpdateCycleDiscountActiveMutation,
  useVbsTreatmentUpdateCycleDiscountActiveAllMutation,
  useVbsTreatmentUpdateCycleDiscountPercentageMutation,
  useVbsTreatmentUpdateSpecialDiscountActiveMutation,
  useVbsTreatmentUpdateSpecialDiscountAmountMutation,
  useVbsTreatmentUpdatePositionCompletionMutation,
  useVbsTreatmentUpdateActuallyTreatedMutation,
  useVbsTreatmentUpdateRemarkMutation,
  useVbsTreatmentSetImmutableMutation,
  useVbsTreatmentDeleteMutation,
} = treatmentApi;

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  ownerType: string;
  ownerId: string;
}

export interface CreateFromOldRequest {
  oldId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  ownerType: string;
  ownerId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  ownerType?: Filter<string>;
  owner?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export type ListAvailableComponentKindsRequest = object;

export interface AvailableComponentKinds {
  [segmentKind: string]: Kind[];
}

export interface CopyBaseDataRequest {
  id: string;
  sourceId: string;
}

export interface AddAreaRequest {
  id: string;
  segment: SegmentKind;
  area: string;
}

export interface RenameAreaRequest {
  id: string;
  segment: SegmentKind;
  old: string;
  new: string;
}

export interface DeleteAreaRequest {
  id: string;
  segment: SegmentKind;
  area: string;
}

export interface AddPositionRequest {
  id: string;
  positionId: string;
  segment: SegmentKind;
  area: string;
  componentKind: Kind;
}

export interface DeletePositionRequest {
  id: string;
  positionId: string;
}

export interface UpdateComponentRequest {
  id: string;
  positionId: string;
  component: Component;
}

export interface UpdateCycleRequest {
  id: string;
  positionId: string;
  cycle: number;
}

export interface UpdateCommentRequest {
  id: string;
  positionId: string;
  comment: string;
}

export interface UpdateFormulaRequest {
  id: string;
  positionId: string;
  formula: Formula;
}

export interface UpdateCycleDiscountActiveRequest {
  id: string;
  positionId: string;
  active: boolean;
}

export interface UpdateCycleDiscountActiveAllRequest {
  id: string;
  active: boolean;
}

export interface UpdateCycleDiscountPercentageRequest {
  id: string;
  positionId: string;
  percentage: number;
}

export interface UpdateSpecialDiscountActiveRequest {
  id: string;
  positionId: string;
  active: boolean;
}

export interface UpdateSpecialDiscountAmountRequest {
  id: string;
  positionId: string;
  amount: number;
}

export interface UpdateActuallyTreatedRequest {
  id: string;
  positionId: string;
  component: Component;
}

export interface UpdatePositionCompletion {
  id: string;
  positionId: string;
  completed: boolean;
  forceCompletion: boolean;
}

export interface UpdateRemarkRequest {
  id: string;
  remark: string;
}

export interface SetImmutableRequest {
  id: string;
  reason: string;
}

export interface DeleteRequest {
  id: string;
}
